/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { FieldArray, FieldArrayRenderProps } from 'formik'
import { useServiceContext } from '../../../../../root'
import { APPROVAL_ACTION_TYPE } from './constants'
import { Select } from '../../../../../components/Select/Select'
import SelectUserGroup from './SelectUserGroup'
import { ErrorMessageStyled } from '../../../../../components/shared/ErrorMessage'
import TextArea from '@mdsol/lego/lib/TextArea'
import FsiPickerModal from '../../../../../components/modals/FsiPickerModal'
import SetPropertyActions from './SetPropertyActions'

interface ApprovalAction {
  actionType: string
  actionData: any
  usersGroups: any[]
  selectedFolder?: number // Folder ID for COPY/MOVE actions (backend compatibility)
}

interface SelectOption {
  value: string
  label: string
}

interface ApprovalEventActionsProps {
  name: string
  values: ApprovalAction[]
}

const ApprovalEventActions: React.FC<ApprovalEventActionsProps> = ({ name, values }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const arrayHelpersRef = useRef<FieldArrayRenderProps | null>(null)
  const [isFolderPickerOpen, setIsFolderPickerOpen] = useState(false)
  const [currentFolderIndex, setCurrentFolderIndex] = useState<number | null>(null)

  const actionTypes = [
    { value: APPROVAL_ACTION_TYPE.COPY, label: translate('APPROVEACTION_COPY_DOC') },
    { value: APPROVAL_ACTION_TYPE.MOVE, label: translate('APPROVEACTION_MOVE_DOC') },
    { value: APPROVAL_ACTION_TYPE.SET_PROPERTY, label: translate('APPROVEACTION_SET_PROPERTY') },
    { value: APPROVAL_ACTION_TYPE.NOTIFY, label: translate('APPROVEACTION_NOTIFY_USERS_GROUPS') },
    { value: APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM, label: translate('APPROVEACTION_NOTIFY_EXTERNAL_SYSTEM') }
  ]

  // Initialize with empty array if values is undefined or empty
  useEffect(() => {
    if (arrayHelpersRef.current && (!values || values.length === 0)) {
      // Don't auto-add any steps for approval events - user must explicitly add them
    }
  }, [values])

  const addStep = (arrayHelpers: FieldArrayRenderProps) => {
    const newStep: ApprovalAction = {
      actionType: '',
      actionData: '',
      usersGroups: []
    }
    arrayHelpers.push(newStep)
  }

  const removeStep = (arrayHelpers: FieldArrayRenderProps, index: number) => {
    arrayHelpers.remove(index)
  }

  const updateStep = (arrayHelpers: FieldArrayRenderProps, newData: ApprovalAction, index: number) => {
    arrayHelpers.replace(index, newData)
  }

  const openFolderPicker = (index: number) => {
    setCurrentFolderIndex(index)
    setIsFolderPickerOpen(true)
  }

  const handleFolderSelect = useCallback(
    (fsi: any) => {
      if (arrayHelpersRef.current && currentFolderIndex !== null && fsi && fsi.id) {
        // Create a new object instead of mutating the existing one
        const item = values[currentFolderIndex]

        const updatedItem = {
          ...item,
          selectedFolder: fsi.id, // Store folder ID for backend
          actionData: {
            // Store object for display
            id: fsi.id,
            name: fsi.name
          }
        }

        updateStep(arrayHelpersRef.current, updatedItem, currentFolderIndex)
      }
      setIsFolderPickerOpen(false)
      setCurrentFolderIndex(null)
    },
    [currentFolderIndex, values]
  )

  const handleActionTypeChange = (
    arrayHelpers: FieldArrayRenderProps,
    option: SelectOption | null,
    item: ApprovalAction,
    index: number
  ) => {
    let updatedItem: ApprovalAction

    if (!option) {
      updatedItem = {
        ...item,
        actionType: '',
        actionData: '',
        usersGroups: []
      }
    } else {
      // Set appropriate default actionData based on type
      let defaultActionData: any
      if (option.value === APPROVAL_ACTION_TYPE.SET_PROPERTY) {
        defaultActionData = []
      } else if (option.value === APPROVAL_ACTION_TYPE.NOTIFY) {
        defaultActionData = ''
      } else if (option.value === APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM) {
        defaultActionData = ''
      } else {
        // COPY or MOVE
        defaultActionData = {}
      }

      updatedItem = {
        ...item,
        actionType: option.value,
        actionData: defaultActionData
      }
    }

    updateStep(arrayHelpers, updatedItem, index)
  }

  return (
    <FieldArray name={name}>
      {(arrayHelpers) => {
        arrayHelpersRef.current = arrayHelpers
        const items = values || []

        return (
          <div className="form-group">
            <div className="custom-dynamic-actions-table">
              {items.length > 0 && (
                <div className="table-header">
                  <span className="h event-type pl5">
                    {translate('BROWSER_FILE_SETTINGS_APPROVERS_TH_EVENT_TYPE')}
                    {/* TODO: Add info popover button like in legacy */}
                  </span>
                  {/* Hide notification recipients column if only one action and it's NOTIFY_EXTERNAL_SYSTEM */}
                  {!(items.length === 1 && items[0].actionType === APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM) && (
                    <span className="h event-type-val">
                      {translate('BROWSER_FILE_SETTINGS_APPROVERS_TH_NOTIFICATION_RECIPIENTS')}
                    </span>
                  )}
                </div>
              )}

              {items.map((item, index) => (
                <div key={index} className="table-row pl5 pr15 py5">
                  <a
                    className="close"
                    onClick={(e) => {
                      e.preventDefault()
                      removeStep(arrayHelpers, index)
                    }}
                  ></a>

                  <div className="clearfix">
                    <div className="h event-type pull-left pt0">
                      <Select
                        options={actionTypes}
                        placeholder={translate('APPROVEACTION_SELECT_EVENT')}
                        isClearable={false}
                        isSearchable={true}
                        id="f66bd4f2-8864-4287-9ad7-b51d1801320f"
                        name={`${name}.${index}.actionType`}
                        onChange={(option) =>
                          handleActionTypeChange(arrayHelpers, option as SelectOption | null, item, index)
                        }
                        value={item.actionType}
                      />
                      <ErrorMessageStyled name={`${name}.${index}.actionType`} />
                    </div>

                    {item.actionType !== APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM ? (
                      <div className="h event-type-val pull-left">
                        <SelectUserGroup
                          isMulti={true}
                          value={item.usersGroups}
                          onChange={(option) => {
                            const updatedItem = {
                              ...item,
                              usersGroups: option ? option : []
                            }
                            updateStep(arrayHelpers, updatedItem, index)
                          }}
                          name={`${name}.${index}.usersGroups`}
                        />
                        <ErrorMessageStyled name={`${name}.${index}.usersGroups`} />
                      </div>
                    ) : (
                      <div className="h event-type-val pull-left">
                        <div className="pl10 ml5">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: translate('EVENT_TYPE_NOTIFY_EXTERNAL_SYSTEM_DESCRIPTION_FOR_USERS')
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="py5">
                    {item.actionType === APPROVAL_ACTION_TYPE.NOTIFY && (
                      <TextArea
                        {...({
                          name: `${name}.${index}.actionData`,
                          id: 'f4733cc1-f2d7-4ef2-ab7f-67e8804d24d3',
                          className: 'form-control',
                          maxLength: 4094,
                          rows: 6,
                          value: item.actionData || '',
                          onChange: (e: any) => {
                            const updatedItem = {
                              ...item,
                              actionData: e.target.value
                            }
                            updateStep(arrayHelpers, updatedItem, index)
                          }
                        } as any)}
                      />
                    )}

                    {(item.actionType === APPROVAL_ACTION_TYPE.MOVE ||
                      item.actionType === APPROVAL_ACTION_TYPE.COPY) && (
                      <>
                        <div className="h event-type py5 pl5">
                          <strong className="mr10">
                            {translate('BROWSER_FILE_SETTINGS_APPROVERS_DESTINATION_FOLDER_LABEL')}:
                          </strong>
                          <p
                            className={'fsi-picker-input' + (item.actionData?.name ? '' : ' empty')}
                            onClick={() => openFolderPicker(index)}
                            style={{ display: 'inline-block', margin: 0 }}
                          >
                            {item.actionData?.name || translate('-SELECT_FOLDER-')}
                          </p>
                        </div>
                        <ErrorMessageStyled name={`${name}.${index}.actionData`} />
                      </>
                    )}

                    {item.actionType === APPROVAL_ACTION_TYPE.SET_PROPERTY && (
                      <SetPropertyActions
                        name={`${name}.${index}.actionData`}
                        value={item.actionData || []}
                        onChange={(newActionData) => {
                          const updatedItem = {
                            ...item,
                            actionData: newActionData
                          }
                          updateStep(arrayHelpers, updatedItem, index)
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              data-action={'add-event'}
              className="btn btn-chita btn-add-step btn-second at_btnAddStepApprover"
              type="button"
              onClick={() => addStep(arrayHelpers)}
            >
              {translate('BROWSER_FILE_SETTINGS_APPROVERS_BTN_ADD_EVENT')}
            </button>

            {isFolderPickerOpen &&
              (() => {
                const selectedId =
                  currentFolderIndex !== null ? values[currentFolderIndex]?.actionData?.id || null : null
                return (
                  <FsiPickerModal
                    options={{
                      selector: 'folder',
                      showRC: true,
                      showSystem: true,
                      selectedId: selectedId
                    }}
                    title={translate('SELECT_FOLDER')}
                    isOpen={isFolderPickerOpen}
                    onClose={() => {
                      setIsFolderPickerOpen(false)
                      setCurrentFolderIndex(null)
                    }}
                    onSelect={handleFolderSelect}
                  />
                )
              })()}
          </div>
        )
      }}
    </FieldArray>
  )
}

export default ApprovalEventActions
